import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../../api/invoices';
import { personsApi } from '../../api/persons';
import { taxRatesApi } from '../../api/taxRates';
import { accountsApi } from '../../api/accounts';
import { cround, formatMoney } from '../../utils/formatMoney';
import { today } from '../../utils/formatDate';
import DatePickerInput from '../../components/DatePickerInput';
import AIPrefillBanner from '../../components/AIPrefillBanner';
import type { CreateInvoiceLineRequest, CreateInvoiceRequest } from '../../types/invoice';
import { useDocumentSerialPreview } from '../../hooks/useDocumentSerial';
import { useAppBack, useBeforeUnload } from '../../hooks/useAppBack';
import { useIsMirror } from '../../hooks/useIsMirror';

interface LineForm {
  name: string;
  unit: string;
  account: string;
  vatAccount: string;
  unitPrice: string;
  quantity: string;
  vatRateId: string;
}

const emptyLine = (): LineForm => ({
  name: '',
  unit: 'vnt.',
  account: '',
  vatAccount: '',
  unitPrice: '0',
  quantity: '1',
  vatRateId: '',
});

export default function InvoiceCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — a
  // deep-linked create route gets a notice instead of the form; the API
  // 403s writes anyway (defense in depth).
  const isMirror = useIsMirror();

  // Read AI pre-fill data from navigation state (ephemeral, gone on refresh)
  const aiPrefill = (location.state as { aiPrefill?: { formType?: string; fields?: Record<string, unknown>; lines?: unknown[]; explanation?: string } } | null)?.aiPrefill;

  const [direction, setDirection] = useState<'Made' | 'Received'>(
    (aiPrefill?.fields?.direction as 'Made' | 'Received') || 'Made'
  );
  const [date, setDate] = useState(
    (aiPrefill?.fields?.date as string) || today()
  );
  const [documentNumber, setDocumentNumber] = useState(
    (aiPrefill?.fields?.documentNumber as string) || ''
  );
  const [content, setContent] = useState(
    (aiPrefill?.fields?.content as string) || ''
  );
  const [personId, setPersonId] = useState(
    aiPrefill?.fields?.personId != null ? String(aiPrefill.fields.personId) : ''
  );
  const [currencyCode, setCurrencyCode] = useState(
    (aiPrefill?.fields?.currencyCode as string) || 'EUR'
  );
  const [currencyRate, setCurrencyRate] = useState('1');
  const [lines, setLines] = useState<LineForm[]>(() => {
    if (aiPrefill?.lines && Array.isArray(aiPrefill.lines) && aiPrefill.lines.length > 0) {
      return aiPrefill.lines.map((l: unknown) => {
        const line = l as Record<string, unknown>;
        return {
          name: (line.name as string) || '',
          unit: (line.unit as string) || 'vnt.',
          account: line.account != null ? String(line.account) : '',
          vatAccount: '',
          unitPrice: line.unitPrice != null ? String(line.unitPrice) : '0',
          quantity: line.quantity != null ? String(line.quantity) : '1',
          vatRateId: '',
        };
      });
    }
    return [emptyLine()];
  });
  const [error, setError] = useState('');
  const [aiPrefilled, setAiPrefilled] = useState(!!aiPrefill);
  // Finding 35: unsaved-invoice guard — USER edits only (form onChange
  // delegation; the auto-number/prefill programmatic sets never fire it).
  const [dirty, setDirty] = useState(false);
  useAppBack({ enabled: true, blocking: dirty });
  useBeforeUnload(dirty);

  // Auto-numbering from document serial
  const serialType = direction === 'Made' ? 'SalesInvoice' : 'PurchaseInvoice';
  const { previewNumber, autoSerialCode, autoFilled, setAutoFilled } = useDocumentSerialPreview(serialType, date);

  // Auto-fill document number from serial when no explicit number set
  useEffect(() => {
    if (previewNumber && !documentNumber && !aiPrefill?.fields?.documentNumber && !autoFilled) {
      setDocumentNumber(previewNumber);
      setAutoFilled(true);
    }
  }, [previewNumber, documentNumber, aiPrefill, autoFilled, setAutoFilled]);

  // Reset auto-fill when direction changes
  useEffect(() => {
    setAutoFilled(false);
    setDocumentNumber('');
  }, [direction, setAutoFilled]);

  const { data: persons } = useQuery({
    queryKey: ['persons-all'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: taxRates } = useQuery({
    queryKey: ['tax-rates-all'],
    queryFn: taxRatesApi.list,
  });

  const createMutation = useMutation({
    mutationFn: invoicesApi.create,
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices/${id}?direction=${direction}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateLine = (index: number, field: keyof LineForm, value: string) => {
    setLines((prev) => {
      const next = [...prev];
      const current = next[index];
      if (current) {
        next[index] = { ...current, [field]: value } as LineForm;
      }
      return next;
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const getTaxRate = (id: string) => {
    if (!id) return 0;
    return taxRates?.find((r) => r.id === Number(id))?.rate ?? 0;
  };

  const buildLineRequest = (line: LineForm): CreateInvoiceLineRequest => {
    const unitPrice = parseFloat(line.unitPrice) || 0;
    const quantity = parseFloat(line.quantity) || 0;
    const vatRate = getTaxRate(line.vatRateId);
    const sum = cround(unitPrice * quantity);
    const sumVat = cround(sum * vatRate);

    return {
      name: line.name,
      unit: line.unit || 'vnt.',
      account: parseInt(line.account) || 0,
      vatAccount: line.vatAccount ? parseInt(line.vatAccount) : null,
      unitPrice,
      quantity,
      vatRate,
      sum,
      sumVat,
      sumOriginal: sum,
      sumVatOriginal: sumVat,
      includeVatInObject: false,
      vatIsVirtual: false,
      declarationSchemaId: 0,
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const explicitNumber = documentNumber.trim();
    const hasSerial = !!autoSerialCode;
    if (!explicitNumber && !hasSerial) { setError('Reikalingas dokumento numeris. Nustatymuose sukonfigūruokite numeraciją arba įveskite rankiniu būdu.'); return; }
    if (!content.trim()) { setError('Turinys yra būtinas'); return; }
    if (!personId) { setError(`${direction === 'Made' ? 'Klientas' : 'Tiekėjas'} yra būtinas`); return; }

    const lineRequests = lines.map(buildLineRequest);
    const invalidLine = lineRequests.find((l) => !l.account || !l.name);
    if (invalidLine) { setError('Kiekviena eilutė turi turėti pavadinimą ir sąskaitą'); return; }

    // If no explicit document number, request auto-generation from server
    const payload: CreateInvoiceRequest = {
      direction,
      date,
      documentNumber: explicitNumber,
      content,
      personId: parseInt(personId),
      currencyCode,
      currencyRate: parseFloat(currencyRate) || 1,
      lines: lineRequests,
      autoSerialCode: !explicitNumber && autoSerialCode ? autoSerialCode : undefined,
    };

    createMutation.mutate(payload);
  };

  // Compute totals for display
  const computedLines = lines.map(buildLineRequest);
  const totalSum = computedLines.reduce((acc, l) => acc + l.sum, 0);
  const totalVat = computedLines.reduce((acc, l) => acc + l.sumVat, 0);

  // Mirror gate (after all hooks): notice instead of the create form.
  if (isMirror) {
    return (
      <div className="max-w-4xl space-y-4">
        <h1 className="text-xl font-bold text-gray-900">Nauja sąskaita faktūra</h1>
        <p className="text-sm text-gray-500">Tik skaitymo režimas</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Nauja sąskaita faktūra</h1>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          onClick={() => navigate(-1)}
        >
          Atšaukti
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
      )}

      {aiPrefilled && (
        <AIPrefillBanner
          explanation={aiPrefill?.explanation}
          onDismiss={() => setAiPrefilled(false)}
        />
      )}

      <form onChange={() => setDirty(true)} onSubmit={handleSubmit} className="space-y-4">
        {/* Direction */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Kryptis</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={direction}
                onChange={(e) => setDirection(e.target.value as 'Made' | 'Received')}
              >
                <option value="Made">Pardavimas (išrašyta)</option>
                <option value="Received">Pirkimas (gauta)</option>
              </select>
            </div>
            <DatePickerInput
              label="Data"
              value={date}
              onChange={setDate}
              required
            />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Dokumento nr.</label>
              <input
                type="text"
                className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md ${!documentNumber && previewNumber ? 'placeholder:text-gray-400' : ''}`}
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                placeholder={previewNumber || 'pvz. SF0001'}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{direction === 'Made' ? 'Klientas' : 'Tiekėjas'}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                required
              >
                <option value="">-- Pasirinkite --</option>
                {persons?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Valiuta</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Valiutos kursas</label>
              <input
                type="number"
                step="0.0001"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={currencyRate}
                onChange={(e) => setCurrencyRate(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Turinys</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Sąskaitos aprašymas…"
                required
              />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold text-sm">Eilutės</h2>
            <button
              type="button"
              className="px-3 py-1 text-xs bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
              onClick={addLine}
            >
              + Pridėti eilutę
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left min-w-32">Pavadinimas</th>
                  <th className="text-left w-20">Vnt.</th>
                  <th className="text-right w-20">Sąskaita</th>
                  <th className="text-right w-20">Kaina</th>
                  <th className="text-right w-16">Kiekis</th>
                  <th className="text-left w-28">PVM tarifas</th>
                  <th className="text-right w-20">Suma</th>
                  <th className="text-right w-20">PVM</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const built = buildLineRequest(line);
                  return (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.name}
                          onChange={(e) => updateLine(i, 'name', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.unit}
                          onChange={(e) => updateLine(i, 'unit', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.account}
                          onChange={(e) => updateLine(i, 'account', e.target.value)}
                          required
                        >
                          <option value="">--</option>
                          {accounts?.map((a) => (
                            <option key={a.accountNumber} value={a.accountNumber}>
                              {a.accountNumber} {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.unitPrice}
                          onChange={(e) => updateLine(i, 'unitPrice', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded num"
                          value={line.quantity}
                          onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="w-full px-1 py-0.5 text-sm border border-gray-200 rounded"
                          value={line.vatRateId}
                          onChange={(e) => updateLine(i, 'vatRateId', e.target.value)}
                        >
                          <option value="">Be PVM</option>
                          {taxRates?.filter((r) => !r.isObsolete).map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name} ({(r.rate * 100).toFixed(0)}%)
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="num">{formatMoney(built.sum, false)}</td>
                      <td className="num">{formatMoney(built.sumVat, false)}</td>
                      <td>
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-600 text-sm"
                          onClick={() => removeLine(i)}
                          disabled={lines.length <= 1}
                        >
                          x
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="text-right font-semibold">Viso</td>
                  <td className="num font-semibold">{formatMoney(totalSum)}</td>
                  <td className="num font-semibold">{formatMoney(totalVat)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={6} className="text-right font-bold">Viso su PVM</td>
                  <td colSpan={2} className="num font-bold text-primary-700">{formatMoney(totalSum + totalVat)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Kuriama…' : 'Sukurti sąskaitą faktūrą'}
          </button>
        </div>
      </form>
    </div>
  );
}
